import { Component, Inject, Input, OnInit } from '@angular/core';
import { EquipmentManagenementService } from '../../../services/equipmentmanagement.service';
import { MatTableDataSource } from '@angular/material/table';
import { APICallStatus, BuildReleaseType } from '../../../utils/utils';
import { TimeSpan } from '../../../utils/TimeSpan';
import { CustomDialogService } from '../../../services/customdialog.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SignalRService } from '../../../services/signal-r';
import { AzurePipelineStatusViewModel, AzurePipelineStatusEventType } from '../../../interfaces/azurepipelineptatusviewmodel';
import { environment } from '../../../../environments/environment';
import { SpinnerInterceptor } from 'src/app/interceptors/spinner.interceptor';
import { SpinnerOverlayService } from 'src/app/services/spinner-overlay.service';

const TYPE_CONFIG: Record<string, {
    buildReleaseType: BuildReleaseType,
    label: string,
}> = {
    emttdc: {
        buildReleaseType: BuildReleaseType.TdcEmt,
        label: 'TDC/EMT - Deployment Status',
    },
    globaltasklist: {
        buildReleaseType: BuildReleaseType.GlobalTaskList,
        label: 'GlobalTaskList - Deployment Status',
    },
};

@Component({
    selector: 'emttdc-deploymentstatus-popup',
    templateUrl: './emttdc-deploymentstatus-popup.component.html',
    styleUrls: ['./emttdc-deploymentstatus-popup.component.less', '../../../style/new-generic-styles.component.less'],
    standalone: false
})
export class EmtTdcDeploymentStatusPopup implements OnInit {
    constructor(
        private emtConfigurationService: EquipmentManagenementService,
        private customDialog: CustomDialogService,
        private spinnerService: SpinnerOverlayService,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<EmtTdcDeploymentStatusPopup>,
        public signalRService: SignalRService
    ) {
        this.environments = [] as EnviromentViewModel[];
        if (data && data.component && TYPE_CONFIG[data.component]) {
            this.InfoTypeConf = TYPE_CONFIG[data.component];
        }
    }
    public BuildReleaseType = BuildReleaseType
    public dataSource = new MatTableDataSource<EnviromentViewModel>()
    public pipelineStatusMessage: string = ''
    public extraStatusMessage: string = ''
    public buildCurrentStatus: AzurePipelineStatusEventType = AzurePipelineStatusEventType.None
    public showBuildSpinner: boolean = false
    public showDeploySpinner: boolean = false
    public canDeploy: boolean = true
    public lastDeployDuration: string = ''
    public showDeployDuration: boolean = false

    public currentStage = environment.stage;
    buildRelease: BuildRelease | undefined
    environments: EnviromentViewModel[] | undefined
    prodEnvironment: EnviromentViewModel | undefined
    devEnvironment: EnviromentViewModel | undefined
    stableEnvironment: EnviromentViewModel | undefined
    isReleaseExpired: boolean | undefined
    isLastBuildInProgress: boolean | undefined
    InfoTypeConf = TYPE_CONFIG['emttdc']
    columnsToDisplay = ['environment']
    showTable: boolean = false
    apiResponse: APICallStatus = APICallStatus.NotRequested
    deployStartTimeMs: number = 0
    deployEndTimeMs: number = 0

    ngOnInit(): void {
        this.refreshInformation()

        // Start SignalR connection for buildrelease/notify hub
        this.signalRService.startConnection(1).then(() => {
            this.signalRService.addPipelineStatusReceivedListener((data) => {
            const viewModel = this.mapToAzurePipelineStatusViewModel(data)
            if (this.isBuild(data)) {
                this.updateBuildStatus(viewModel)
            } else if(this.isDeploy(data)) {
                this.updateDeployStatus(viewModel)
            }
            });
        });
    }
    get isLoading(): boolean {
        return this.apiResponse === APICallStatus.Waiting4Response
    }

    get buildExists(): boolean {
        return this.buildRelease ? this.buildRelease?.BuildId !== -1 && !this.isReleaseExpired && !this.isLastBuildInProgress : false
    }

    refreshInformation() {
        this.extraStatusMessage = ''
        this.apiResponse = APICallStatus.Waiting4Response
        this.showTable = false
        this.canDeploy = true

        this.emtConfigurationService.getLastBuildId(
            this.InfoTypeConf.buildReleaseType,
            this,
            (tthis: EmtTdcDeploymentStatusPopup, buildRelease: BuildRelease) => {
                tthis.handleBuildRelease(buildRelease)
            },
            error => this.handleError()
        );
    }

    private handleBuildRelease(buildRelease: BuildRelease) {
        this.buildRelease = buildRelease

        if (!buildRelease || buildRelease.BuildId === -1) {
            this.showTable = false
            this.apiResponse = APICallStatus.ResponseEmptyOk
            this.columnsToDisplay = ['environment']
            this.dataSource.data = this.environments || []
            return
        }

        this.emtConfigurationService.getRelease(
            this.InfoTypeConf.buildReleaseType,
            this,
            (tthis: EmtTdcDeploymentStatusPopup, data: ReleaseInformation) => {
                tthis.handleReleaseData(data)
            },
            error => this.handleError()
        );
    }

    private handleReleaseData(data: ReleaseInformation) {
        this.apiResponse = APICallStatus.ResponseDataOk
        this.isReleaseExpired = data.IsReleaseExpired
        this.isLastBuildInProgress = data.IsReleaseNotCreatedYet

        if (!data.Environments) {
            this.showTable = false
            return;
        }

        this.showTable = true
        this.environments = data.Environments
        this.devEnvironment = data.Environments.find(env => env.Name === 'dev')
        this.stableEnvironment = data.Environments.find(env => env.Name === 'stable')
        this.prodEnvironment = data.Environments.find(env => env.Name === 'ea')
        this.showDeploySpinner = this.isDeployOnGoing()

        if (this.buildRelease) {
            this.buildRelease.ReleaseId = data.ReleaseId
        }

        this.columnsToDisplay = ['environment', 'environmentdeploymentStatus']
        this.dataSource.data = this.getFilteredEnvironments(this.environments)
    }

    private handleError() {
        this.apiResponse = APICallStatus.ResponseError
    }

    getStatusLabel(status: number | string): string {
        switch (status) {
            case 0:
            case 'Undefined':
            case 1:
            case 'NotStarted':
                return 'Pending'
            case 2:
            case 'InProgress':
            case 32:
            case 'Queued':
                return 'In Progress'
            case 64:
            case 'Scheduled':
                return 'Scheduled'
            case 128:
            case 'PartiallySucceeded':
                return 'Partially Done'
            case 4:
            case 'Succeeded':
                return 'Done'
            case 8:
            case 'Canceled':
                return 'Canceled'
            case 16:
            case 'Rejected':
                return 'Rejected'
            default:
                return 'Unknown'
        }
    }

    mapToAzurePipelineStatusViewModel(raw: any): AzurePipelineStatusViewModel {
        return {
            id: raw.id,
            notificationId: raw.notificationId,
            eventType: raw.eventType as AzurePipelineStatusEventType, // cast if enum
            message: raw.message,
            detailedMessage: raw.detailedMessage,
            createdDate: new Date(raw.createdDate), // convert string to Date
            baseUrl: raw.baseUrl,
            stageName: raw.stageName ?? "" // handle null
        }
    }

    isBuild(vm: AzurePipelineStatusViewModel) {
        return vm.eventType === AzurePipelineStatusEventType.BuildQueued || vm.eventType === AzurePipelineStatusEventType.BuildInProgress || vm.eventType === AzurePipelineStatusEventType.BuildFinished
    }

    isDeploy(vm: AzurePipelineStatusViewModel) {
        return vm.eventType === AzurePipelineStatusEventType.DeployStageStarted || vm.eventType === AzurePipelineStatusEventType.DeployStageFinished
    }

    isDeployOnGoing(): boolean {
        return this.environments?.some(x => x.Status === "InProgress") ? true : false
    }

    updateBuildStatus(event: AzurePipelineStatusViewModel) {
        this.buildCurrentStatus = event.eventType
        this.pipelineStatusMessage = event.detailedMessage

        if(event.eventType === AzurePipelineStatusEventType.BuildQueued || event.eventType === AzurePipelineStatusEventType.BuildInProgress) {
            this.showBuildSpinner = true
            this.showTable = false
        }
        else if(event.eventType === AzurePipelineStatusEventType.BuildFinished) {
            this.showBuildSpinner = false
            if(event.detailedMessage.toLowerCase().includes("succeeded")) {
                this.refreshInformation()
            }
            if(event.detailedMessage.toLowerCase().includes("failed")) {
                this.extraStatusMessage = `https://tkebbxdev.visualstudio.com/BackOfficeAppDeployment/_build/results?buildId=${event.id}&view=logs`
                this.canDeploy = false
            }
        }
    }

    updateDeployStatus(event: AzurePipelineStatusViewModel) {
        let env = this.environments?.find(x => x.Name === event.stageName)

        if (env && event.eventType === AzurePipelineStatusEventType.DeployStageStarted) {
            env.Status = "InProgress"
        }
        else if(env && event.eventType === AzurePipelineStatusEventType.DeployStageFinished) {
            env.Status = "Succeeded"
        }

        if(this.isDeployOnGoing()) {
            this.showDeploySpinner = true
        }
        else {
            this.showDeploySpinner = false
            this.deployEndTimeMs = Date.now()
            this.lastDeployDuration = TimeSpan.fromMilliseconds(this.deployEndTimeMs - this.deployStartTimeMs).toStringHMS()
            this.showDeployDuration = true;
        }
    }

    updateToBuildQueued() {
        const model: AzurePipelineStatusViewModel = {
            id: 0,
            notificationId: 0,
            eventType: AzurePipelineStatusEventType.BuildQueued,
            message: '',
            detailedMessage: 'Build queued',
            createdDate: new Date(),
            baseUrl: '',
            stageName: ''
        };
        this.buildRelease = undefined
        this.extraStatusMessage = ''
        this.canDeploy = true
        this.updateBuildStatus(model)
    }

    onClickDeploy() {
        this.customDialog.openConfirm({
            title: "Create deploy",
            message: `A new build will start immediately. Do you confirm?`,
            caller: this
        });
        this.customDialog.confirmed().subscribe(res => {
            if (res.confirmed === true) {
                this.apiResponse = APICallStatus.Waiting4Response;
                this.emtConfigurationService.createBuildForEnv(
                    this.InfoTypeConf.buildReleaseType,
                    (data: number) => {
                        if (data) {
                            this.waitForDeploymentToFinish(data);
                        }
                        else
                            this.customDialog.openDisplayInfoDialog({ title: 'Error', message: 'Error executing deployment. Please, contact DevOps team.' });
                    }
                    , error => {
                        this.apiResponse = APICallStatus.ResponseError;
                    });
            }
        });
    }

    waitForDeploymentToFinish(deploymentId: number) {
        const spinnerSubscription = this.spinnerService.spinner$().subscribe();
        const intervalPolling = setInterval(() => {
            this.emtConfigurationService.getDeploymentStatus(
                deploymentId,
                (result: string) => {
                    if (result) {
                        console.log('[Status] ' + result);

                        if (result?.indexOf('OK -') === 0) {
                            clearInterval(intervalPolling);
                            spinnerSubscription.unsubscribe();
                            this.apiResponse = APICallStatus.ResponseDataOk;
                            this.customDialog.openDisplayInfoDialog({ title: 'Deployment started', message: 'Please wait until build finishes for releasing it into desired environment.' });
                            this.updateToBuildQueued() 
                        }
                        else if (/^(ERROR|FAILED)\s-/.test(result)) {
                            clearInterval(intervalPolling);
                            spinnerSubscription.unsubscribe();
                            this.customDialog.openDisplayInfoDialog({ title: 'Error', message: 'Error executing deployment. Please, contact DevOps team.' });
                            this.apiResponse = APICallStatus.ResponseError;
                        }
                    }
                },
                error => {
                    clearInterval(intervalPolling);
                    spinnerSubscription.unsubscribe();
                    this.customDialog.openDisplayInfoDialog({ title: 'Error', message: 'Error executing deployment. Please, contact DevOps team.' });
                    this.apiResponse = APICallStatus.ResponseError;
                }
            )
        }, 3000)
    }

    releaseEnvironment(envId: number) {
        this.extraStatusMessage = ''
        this.customDialog.openConfirm({
            title: "New deploy",
            message: `A new deploy will start immediately. Do you confirm?`,
            caller: this
        });
        this.customDialog.confirmed().subscribe(res => {
            if (res.confirmed === true) {
                this.emtConfigurationService.releaseEnvironment(
                    this.InfoTypeConf.buildReleaseType,
                    this
                    , envId
                    , (tthis: EmtTdcDeploymentStatusPopup, data: boolean) => {
                        if (data === true) {
                            tthis.customDialog.openDisplayInfoDialog({ title: 'Release started', message: 'Please wait until deployment finishes. Use refresh button to update the information.' });
                            tthis.refreshInformation();
                            this.deployStartTimeMs = Date.now()
                        }
                        else
                            tthis.customDialog.openDisplayInfoDialog({ title: 'Error', message: 'Error executing deployment. Please, contact DevOps team.' });
                    }
                );
            }
        })
    }

    backClick(): void {
        this.dialogRef.close();
    }

    private getFilteredEnvironments(environments: EnviromentViewModel[]): EnviromentViewModel[] {
        if (this.currentStage === 2) { // Dev
            return environments.filter(env => env.Name === 'dev');
        } else if (this.currentStage === 0) { // Stable
            return environments.filter(env => env.Name === 'dev' || env.Name === 'stable');
        } else { // Production (stage=1)
            return environments.filter(env => env.Name !== 'dev' && env.Name !== 'stable');
        }
    }
}
